// Story 11.2 — Layout helper. La impl preferida usa `dagre` (DAG TB) pero el
// fallback `simpleRankLayout` no requiere deps y produce un layout
// determinístico aceptable para grafos pequeños — útil cuando dagre no está
// instalado o para los tests.

import type { DiagramGraph, DiagramNode } from './flow-to-graph';

export interface LayoutOptions {
  /** Ancho de cada nodo en px. */
  nodeWidth?: number;
  /** Alto de cada nodo en px. */
  nodeHeight?: number;
  /** Separación horizontal entre nodos en el mismo rank. */
  nodesep?: number;
  /** Separación vertical entre ranks. */
  ranksep?: number;
}

export interface PositionedNode extends DiagramNode {
  position: { x: number; y: number };
  width: number;
  height: number;
}

export interface PositionedGraph {
  nodes: PositionedNode[];
  edges: DiagramGraph['edges'];
}

const DEFAULTS: Required<LayoutOptions> = {
  nodeWidth: 180,
  nodeHeight: 60,
  nodesep: 60,
  ranksep: 80,
};

/**
 * Layout determinístico topdown sin deps externas. Asigna ranks via BFS
 * desde nodos raíz (sin in-edges); empata orden por id ascendente. Suficiente
 * para grafos lineales y branchings simples; para grafos densos `dagre`
 * produce mejor compactness — la versión con dagre vive en `layout-dagre.ts`
 * (lazy-loaded en runtime).
 */
export function simpleRankLayout(
  graph: DiagramGraph,
  options: LayoutOptions = {},
): PositionedGraph {
  const opts = { ...DEFAULTS, ...options };
  const ranks = computeRanks(graph);

  // Agrupa nodos por rank.
  const byRank = new Map<number, DiagramNode[]>();
  for (const n of graph.nodes) {
    const r = ranks.get(n.id) ?? 0;
    const list = byRank.get(r) ?? [];
    list.push(n);
    byRank.set(r, list);
  }

  const positioned: PositionedNode[] = [];
  const sortedRanks = [...byRank.keys()].sort((a, b) => a - b);
  for (const r of sortedRanks) {
    const bucket = byRank.get(r);
    if (!bucket) continue;
    const row = bucket.slice().sort((a, b) => a.id.localeCompare(b.id));
    const totalWidth = row.length * opts.nodeWidth + (row.length - 1) * opts.nodesep;
    const startX = -totalWidth / 2;
    for (let i = 0; i < row.length; i++) {
      const n = row[i];
      positioned.push({
        ...n,
        width: opts.nodeWidth,
        height: opts.nodeHeight,
        position: {
          x: startX + i * (opts.nodeWidth + opts.nodesep),
          y: r * (opts.nodeHeight + opts.ranksep),
        },
      });
    }
  }

  return { nodes: positioned, edges: graph.edges };
}

/**
 * Computa rank (distancia topológica desde un root) de cada nodo. Usa BFS
 * sobre los edges; si hay ciclos (e.g. goto-back), corta y mantiene el rank
 * más bajo encontrado. Ghost nodes (`goto-ghost`) reciben rank = max(rank
 * de cualquier source) + 1 para que aparezcan al final.
 */
export function computeRanks(graph: DiagramGraph): Map<string, number> {
  const inEdges = new Map<string, string[]>();
  for (const n of graph.nodes) inEdges.set(n.id, []);
  for (const e of graph.edges) {
    inEdges.get(e.target)?.push(e.source);
  }
  // Roots: nodos sin in-edges (typically el primer step).
  const roots = graph.nodes.filter((n) => (inEdges.get(n.id) ?? []).length === 0).map((n) => n.id);

  const rank = new Map<string, number>();
  const visited = new Set<string>();

  function bfs(seeds: string[]): void {
    const queue = [...seeds].sort((a, b) => a.localeCompare(b));
    for (const s of queue) {
      if (!rank.has(s)) rank.set(s, 0);
      visited.add(s);
    }
    while (queue.length > 0) {
      const id = queue.shift() as string;
      const cur = rank.get(id) ?? 0;
      const outgoing = graph.edges.filter((e) => e.source === id);
      for (const e of outgoing) {
        const existing = rank.get(e.target);
        const candidate = cur + 1;
        const tRank = existing != null && existing >= candidate ? existing : candidate;
        rank.set(e.target, tRank);
        if (!visited.has(e.target)) {
          visited.add(e.target);
          queue.push(e.target);
        }
      }
    }
  }

  // P-361 (review 11.2): BFS multi-source desde roots reales. Si después
  // del primer pase queda algún nodo sin visitar (graph con un ciclo donde
  // todos tienen in-edges, o múltiples componentes desconectados), hacemos
  // pases adicionales tomando el id no-visitado más bajo como root sintético.
  // Sin esto, los grafos con ciclos completos colapsaban todos los nodos al
  // rank 0 (todos al mismo y → diagrama ilegible).
  bfs(roots);
  while (visited.size < graph.nodes.length) {
    const next = graph.nodes
      .map((n) => n.id)
      .filter((id) => !visited.has(id))
      .sort((a, b) => a.localeCompare(b))[0];
    if (!next) break;
    bfs([next]);
  }

  // Asegura que cada nodo tenga rank (defensivo; el bfs de arriba lo cubre).
  for (const n of graph.nodes) {
    if (!rank.has(n.id)) rank.set(n.id, 0);
  }
  return rank;
}

/**
 * Aplica el layout que esté disponible. P-360/P-364 (review 11.2): el
 * runtime puede precargar `dagreLayout` (de `layout-dagre.ts`) cuando las
 * deps de UI estén instaladas y se quiera el layout dagre real (AC #5).
 * Por default cae al `simpleRankLayout` puro — determinístico y sin deps,
 * apto para tests y para entornos sin `dagre` instalado.
 *
 * Para activar dagre en producción: en el bootstrap del editor (e.g.,
 * `EditorPage` mount), llamar `setLayoutImpl(dagreLayout)` con la función
 * importada desde `./layout-dagre.js`. La separación evita que los tests
 * pure-logic (sin deps de UI) requieran `dagre`.
 */
let layoutImpl: (graph: DiagramGraph, options?: LayoutOptions) => PositionedGraph =
  simpleRankLayout;

export function setLayoutImpl(
  fn: (graph: DiagramGraph, options?: LayoutOptions) => PositionedGraph,
): void {
  layoutImpl = fn;
}

export function layoutGraph(graph: DiagramGraph, options?: LayoutOptions): PositionedGraph {
  return layoutImpl(graph, options);
}
